import React, { Fragment } from 'react';
import {
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  AreaChart,
  Area,
  ResponsiveContainer,
} from 'recharts';
import { Typography, Flex, Skeleton } from 'antd';
import CustomTooltip from './CustomTooltip';
import getFormattedNumber from 'store/redux/utils/getFormattedNumber';
import getFormattedValue from 'store/redux/utils/getFormattedValue';
import { integrationMetricList } from 'modules/DashboardModule/charts/components/IntegrationMetricList';
import { NameDefinition } from 'components/NameDefinition';
import { useWidgetDate } from 'store/redux/states/dates/dates.hooks';
import { useWidget } from 'components/Widget/useWidget';
import Icon from '@mdi/react';
import './style.css';
import Ninja from '@ninja';
import getShortFormattedNumber from 'store/redux/utils/getShortFormattedNumber';

const WidgetCharts = ({
  data,
  loading,
  index,
  label,
  totals,
  icon,
  existDefinition,
  definition,
  exctraAction,
  metricList,
}) => {
  const { chartConfig, widget_key } = useWidget();
  const { date_from, date_to } = useWidgetDate(widget_key);

  const metricDefinition = (metricList || integrationMetricList)[index] || {};
  const { symbol, currency, valueFormatter, formatter } = metricDefinition;

  let formattedValue = totals && totals[index];
  if (symbol && totals) {
    formattedValue = `${getFormattedNumber(totals[index])}${symbol}`;
  } else if (currency && totals) {
    formattedValue = getFormattedValue(totals[index]);
  }

  const ValueFormatter = valueFormatter || Fragment;
  const FormatterComponent = formatter || Fragment;

  return (
    <Skeleton loading={loading} active>
      <div
        className='p-1 rounded-md border border-solid border-color-xlight'
        style={{ width: '100%' }}
      >
        <div className='pb-1 px-2 flex items-center gap-2 justify-between'>
          <Flex gap='small' align='center'>
            {icon && <Icon path={icon} size={0.8} className='text-orange' />}
            {definition ? (
              <NameDefinition
                placement='bottom'
                name={definition.name}
                definition={definition.description}
                textSize='text-[11px] font-semibold w-fit text-background-dark'
              />
            ) : (
              <Typography
                variant='h4'
                className='!text-[11px] font-semibold w-fit text-background-dark'
              >
                {existDefinition || label}
              </Typography>
            )}
            {exctraAction}
          </Flex>

          <Typography variant='h1' className='text-md flex font-medium text-left'>
            <div className='flex flex-wrap flex-center gap-2 show-info-on-hover !text-md'>
              {valueFormatter && totals ? (
                <ValueFormatter value={totals[index] || 0} className='!text-lg' />
              ) : formatter && totals ? (
                <FormatterComponent row={totals} fontSize={16} fontWeight='semibold' />
              ) : (
                formattedValue
              )}
            </div>
          </Typography>
        </div>

        {data?.length > 1 && date_from !== date_to ? (
          <ResponsiveContainer width='100%' height='auto' aspect={3.5 / 1.0}>
            <AreaChart
              stackOffset='sign'
              data={data}
              syncId={widget_key}
              margin={{
                right: chartConfig.showYAxis ? 20 : 0,
                top: 5,
              }}
            >
              <CartesianGrid strokeDasharray='2 2' stroke={Ninja.colors.xlight} vertical={false} />
              <XAxis
                dataKey='date'
                height={20}
                hide={!chartConfig.showXAxis}
                axisLine={false}
                interval={Math.ceil(data.length / 10)}
                tick={{ fontSize: 12, fontWeight: '400', fill: Ninja.colors.dark }}
                tickFormatter={(value) =>
                  new Date(value).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: '2-digit',
                  })
                }
              />
              <YAxis
                axisLine={false}
                margin={0}
                padding={0}
                width={30}
                hide={!chartConfig.showYAxis}
                tick={{ fontSize: 12, fontWeight: '400', fill: Ninja.colors.dark }}
                tickFormatter={(value) => getShortFormattedNumber(value)}
              />
              <Tooltip
                content={
                  <CustomTooltip
                    name={label}
                    dataKey='date'
                    value_key={index}
                    metricsList={metricList}
                  />
                }
                cursor={{
                  stroke: chartConfig?.areaConfig?.strokeColor || '#536A8B',
                  strokeWidth: 1,
                  strokeDasharray: '2 2',
                }}
              />
              <Area
                type='monotone'
                strokeWidth={1}
                dataKey={index}
                stroke={chartConfig?.areaConfig?.strokeColor || '#536A8B'}
                fill={chartConfig?.areaConfig?.fillColor || '#F0F6FF'}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className='flex items-center justify-center w-full'>
            <div className='grid gap-2 mt-4 text-color-light'>
              <span className='font-normal text-xs text-center'>
                Select more than one day to view the chart.
              </span>
              <img src='/icons/no-data.svg' width='100%' />
            </div>
          </div>
        )}
      </div>
    </Skeleton>
  );
};

export default WidgetCharts;
